import { useEffect, useState } from "react";

const NAVBAR_ROUTE_OPEN_HANDOFF_MS = 1000;

// Module-level because the source route unmounts before the destination route can read the handoff.
let openSidebarOnNextRouteUntil = 0;

/**
 * Requests that the next non-auto-collapsed navbar route animate from collapsed to expanded.
 *
 * This smooths the visual handoff when a user leaves an auto-collapsed page, such as SQL Explorer
 * or Settings, from the hover-expanded sidebar. The destination first paints at collapsed width,
 * then opens on the next animation frame so the main pane is pushed by the sidebar transition.
 */
export const preserveOpenSidebarForNextRoute = () => {
  openSidebarOnNextRouteUntil = Date.now() + NAVBAR_ROUTE_OPEN_HANDOFF_MS;
};

const shouldOpenSidebarForRouteHandoff = () => {
  return typeof window !== "undefined" && Date.now() <= openSidebarOnNextRouteUntil;
};

const clearOpenSidebarRouteHandoff = () => {
  openSidebarOnNextRouteUntil = 0;
};

export interface UseNavbarSmoothCollapseOptions {
  routeRequestsCollapsedSidebar: boolean;
  transitionMs: number;
  setSidebarOpen: (sidebarOpen: boolean) => void;
}

/**
 * Consumes a pending cross-route open handoff and expands the sidebar after first paint.
 *
 * The returned boolean should participate in the navbar's collapsed-width calculation after the
 * initial collapsed paint. Once the destination route has committed its real expanded state, the
 * hook clears the temporary handoff so subsequent renders return to normal user-controlled sidebar
 * behavior.
 */
export const useNavbarSmoothCollapse = ({
  routeRequestsCollapsedSidebar,
  transitionMs,
  setSidebarOpen,
}: UseNavbarSmoothCollapseOptions) => {
  const [routeOpenHandoffRequested] = useState(
    () => !routeRequestsCollapsedSidebar && shouldOpenSidebarForRouteHandoff(),
  );
  const [routeOpenHandoffActive, setRouteOpenHandoffActive] = useState(false);

  useEffect(() => {
    if (!routeOpenHandoffRequested) return;

    clearOpenSidebarRouteHandoff();
    const animationFrameId = window.requestAnimationFrame(() => {
      setRouteOpenHandoffActive(true);
      setSidebarOpen(true);
    });
    const timeoutId = window.setTimeout(() => {
      setRouteOpenHandoffActive(false);
    }, transitionMs + 16);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
    };
  }, [routeOpenHandoffRequested, setSidebarOpen, transitionMs]);

  return routeOpenHandoffActive;
};
